import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Logo from './Logo';
import { COMPANY_INFO, NAV_LINKS } from '../data/companyData';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(true);
  const dropdownRef = useRef(null);
  const location = useLocation();

  const isServicesActive = location.pathname.startsWith('/ydelser');

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  // Handle click outside desktop dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="sticky top-0 z-40 bg-neutral-950/95 backdrop-blur-md border-b border-neutral-800 transition-shadow">
      {/* Top Bar for Phone, Address & CVR */}
      <div className="hidden min-[1200px]:block bg-black text-neutral-300 text-xs border-b border-neutral-800/80 py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2">
              <i className="fa-solid fa-location-dot text-[#DFBF77]" aria-hidden="true" />
              <span>{COMPANY_INFO.address.full}</span>
            </span>
            <span className="text-neutral-500">|</span>
            <span className="flex items-center gap-2">
              <i className="fa-solid fa-id-card text-[#DFBF77]" aria-hidden="true" />
              <span>CVR: {COMPANY_INFO.cvr}</span>
            </span>
          </div>

          <div className="flex items-center gap-6">
            <a
              href={COMPANY_INFO.phoneHref}
              className="flex items-center gap-2 text-white hover:text-[#DFBF77] transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-[#C5A059]" aria-hidden="true" />
              <span className="font-semibold whitespace-nowrap">{COMPANY_INFO.phoneFormatted}</span>
            </a>
            <span className="text-neutral-500">|</span>
            <a
              href={COMPANY_INFO.emailHref}
              className="flex items-center gap-2 text-neutral-300 hover:text-white transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-envelope text-[#C5A059]" aria-hidden="true" />
              <span className="whitespace-nowrap">{COMPANY_INFO.email}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Header Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 sm:h-24 py-1.5">
          {/* Logo */}
          <Logo />

          {/* Desktop Navigation (Visible only at >= 1200px as per spec) */}
          <nav className="hidden min-[1200px]:flex items-center gap-1 xl:gap-2">
            {NAV_LINKS.map((link) => {
              if (link.children) {
                return (
                  <div
                    key={link.path}
                    ref={dropdownRef}
                    className="relative"
                    onMouseEnter={() => setDropdownOpen(true)}
                    onMouseLeave={() => setDropdownOpen(false)}
                  >
                    <div className="flex items-center">
                      <Link
                        to={link.path}
                        className={`px-3.5 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-1.5 ${
                          isServicesActive
                            ? 'text-[#DFBF77] bg-neutral-900 font-bold'
                            : 'text-neutral-300 hover:text-white hover:bg-neutral-900/80'
                        }`}
                      >
                        <span>{link.label}</span>
                        <i
                          className={`fa-solid fa-chevron-down text-[11px] transition-transform duration-200 ${
                            dropdownOpen ? 'rotate-180 text-[#DFBF77]' : 'text-neutral-400'
                          }`}
                          aria-hidden="true"
                        />
                      </Link>
                    </div>

                    {/* Dropdown Menu */}
                    {dropdownOpen && (
                      <div className="absolute left-0 top-full pt-1 w-72 animate-fadeIn z-50">
                        <div className="rounded-xl bg-neutral-950 border border-neutral-800 shadow-2xl p-2.5 divide-y divide-neutral-800">
                          <div className="pb-2">
                            <Link
                              to="/ydelser"
                              className="flex items-center justify-between px-3 py-2 rounded-lg text-xs font-bold text-[#DFBF77] bg-neutral-900 hover:bg-neutral-850 hover:text-white transition-colors"
                            >
                              <span>Se alle ydelser</span>
                              <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                            </Link>
                          </div>
                          <div className="pt-2 space-y-1">
                            {link.children.map((sub) => {
                              const isSubActive = location.pathname === sub.path;
                              return (
                                <Link
                                  key={sub.path}
                                  to={sub.path}
                                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium transition-all ${
                                    isSubActive
                                      ? 'text-white font-bold bg-neutral-900 border-l-2 border-[#DFBF77]'
                                      : 'text-neutral-300 hover:text-white hover:bg-neutral-900/60'
                                  }`}
                                >
                                  <div className={`w-7 h-7 rounded-md flex items-center justify-center text-xs flex-shrink-0 ${
                                    isSubActive ? 'bg-[#DFBF77] text-neutral-950' : 'bg-neutral-900 text-neutral-400'
                                  }`}>
                                    <i className={sub.icon} aria-hidden="true" />
                                  </div>
                                  <span className="leading-tight">{sub.label}</span>
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-3.5 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    isActive
                      ? 'text-[#DFBF77] bg-neutral-900 font-bold'
                      : 'text-neutral-300 hover:text-white hover:bg-neutral-900/80'
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* Desktop Right CTA Section (Visible at >= 1200px) */}
          <div className="hidden min-[1200px]:flex items-center gap-4">
            <a
              href={COMPANY_INFO.phoneHref}
              className="flex items-center gap-2 text-sm font-bold text-white hover:text-[#DFBF77] transition-colors whitespace-nowrap"
            >
              <div className="w-8 h-8 rounded-full bg-neutral-900 border border-neutral-800 flex items-center justify-center text-[#DFBF77]">
                <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
              </div>
              <span className="whitespace-nowrap">{COMPANY_INFO.phoneFormatted}</span>
            </a>

            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-[#C5A059] hover:bg-[#DFBF77] text-neutral-950 font-bold text-sm transition-all duration-200 shadow-sm border border-[#C5A059] focus:outline-none focus:ring-2 focus:ring-[#DFBF77]"
            >
              <span>Få et tilbud</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>

          {/* Mobile & Tablet Header Elements (Under 1200px) */}
          <div className="flex min-[1200px]:hidden items-center gap-3">
            <a
              href={COMPANY_INFO.phoneHref}
              className="flex items-center justify-center w-10 h-10 rounded-lg bg-neutral-900 border border-neutral-800 text-[#DFBF77] hover:bg-neutral-850 transition-colors"
              aria-label="Ring til Morine Køl & Klimateknik"
            >
              <i className="fa-solid fa-phone text-sm" aria-hidden="true" />
            </a>

            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="flex items-center justify-center w-10 h-10 rounded-lg bg-neutral-900 text-white hover:bg-neutral-800 border border-neutral-800 transition-colors focus:outline-none focus:ring-2 focus:ring-[#DFBF77]"
              aria-label={mobileMenuOpen ? 'Luk menu' : 'Åbn menu'}
              aria-expanded={mobileMenuOpen}
            >
              <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-lg`} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer Menu (Under 1200px) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden bg-neutral-950 border-b border-neutral-800 shadow-2xl animate-fadeIn">
          <div className="max-w-7xl mx-auto px-4 py-5 space-y-4">
            <nav className="space-y-1">
              <Link
                to="/"
                className={`block px-4 py-3 rounded-lg text-base font-semibold ${
                  location.pathname === '/'
                    ? 'text-white bg-neutral-900 border-l-4 border-[#DFBF77]'
                    : 'text-neutral-200 hover:bg-neutral-900'
                }`}
              >
                Forside
              </Link>

              {/* Mobile Services Accordion */}
              <div>
                <button
                  type="button"
                  onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-base font-semibold text-left ${
                    isServicesActive
                      ? 'text-white bg-neutral-900 border-l-4 border-[#DFBF77]'
                      : 'text-neutral-200 hover:bg-neutral-900'
                  }`}
                >
                  <span>Ydelser</span>
                  <i
                    className={`fa-solid fa-chevron-down text-xs transition-transform ${
                      mobileServicesOpen ? 'rotate-180 text-[#DFBF77]' : 'text-neutral-400'
                    }`}
                    aria-hidden="true"
                  />
                </button>

                {mobileServicesOpen && (
                  <div className="pl-4 pr-2 py-2 space-y-1 bg-neutral-900/70 rounded-lg mt-1 border border-neutral-850">
                    <Link
                      to="/ydelser"
                      className="block px-3 py-2 text-xs font-bold text-[#DFBF77] hover:underline"
                    >
                      Oversigt over alle ydelser →
                    </Link>
                    {NAV_LINKS.find((l) => l.children)?.children.map((sub) => (
                      <Link
                        key={sub.path}
                        to={sub.path}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm ${
                          location.pathname === sub.path
                            ? 'text-white font-bold bg-neutral-950 shadow-xs'
                            : 'text-neutral-300 hover:text-white hover:bg-neutral-950/50'
                        }`}
                      >
                        <i className={`${sub.icon} text-xs text-[#DFBF77] w-4 text-center`} aria-hidden="true" />
                        <span>{sub.label}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/om-os"
                className={`block px-4 py-3 rounded-lg text-base font-semibold ${
                  location.pathname === '/om-os'
                    ? 'text-white bg-neutral-900 border-l-4 border-[#DFBF77]'
                    : 'text-neutral-200 hover:bg-neutral-900'
                }`}
              >
                Om os
              </Link>

              <Link
                to="/kontakt"
                className={`block px-4 py-3 rounded-lg text-base font-semibold ${
                  location.pathname === '/kontakt'
                    ? 'text-white bg-neutral-900 border-l-4 border-[#DFBF77]'
                    : 'text-neutral-200 hover:bg-neutral-900'
                }`}
              >
                Kontakt
              </Link>
            </nav>

            {/* Mobile Contact Quick Actions */}
            <div className="pt-4 border-t border-neutral-800 space-y-3">
              <Link
                to="/kontakt"
                className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg bg-[#C5A059] hover:bg-[#DFBF77] text-neutral-950 font-bold text-sm shadow-md"
              >
                <span>Få et tilbud</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>

              <div className="p-3 bg-neutral-900 border border-neutral-800 rounded-lg text-xs space-y-1.5 text-neutral-300">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-phone text-[#DFBF77]" aria-hidden="true" />
                  <a href={COMPANY_INFO.phoneHref} className="font-bold text-white whitespace-nowrap hover:text-[#DFBF77]">
                    {COMPANY_INFO.phoneFormatted}
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-envelope text-[#DFBF77]" aria-hidden="true" />
                  <a href={COMPANY_INFO.emailHref} className="whitespace-nowrap text-neutral-300 hover:text-white">
                    {COMPANY_INFO.email}
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-location-dot text-[#DFBF77]" aria-hidden="true" />
                  <span>{COMPANY_INFO.address.full}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
